import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { Route, Routes, useParams } from "react-router";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import DocumentationIconButton from "components/DocumentationIconButton";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import MetaInfoList from "components/MetaInfoList";
import PolicyTypeBadge from "components/PolicyTypeBadge";
import SpacePath from "components/SpacePath";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import { POLICY_TYPES_FEATURES, POLICY_TYPES_TITLES } from "constants/policy";
import Button from "ds/components/Button";
import LinkButton from "ds/components/Button/LinkButton";
import Callout from "ds/components/Callout";
import FeedbackActions from "ds/components/Feedback/Actions";
import Tab from "ds/components/Tab";
import TabWithCounter from "ds/components/Tab/WithCounter";
import { AnalyticsPagePolicy } from "hooks/useAnalytics/pages/policy";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import useCanManagePolicy from "shared/Policy/access/useCanManagePolicy";
import { PolicyType } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import PolicyDetailsAskButton from "views/Account/PolicyDetails/AskButton";
import PolicyDetailsManageDropdown from "views/Account/PolicyDetails/ManageDropdown";

import { FILTERS_ORDER_SETTINGS_KEY } from "../Policies/constants";
import useTakenPolicyNames from "../Policies/useTakenPolicyNames";
import { showPolicyEditDrawer } from "../PolicyEditDrawer";
import { SubscriptionContext } from "../SubscriptionWrapper";
import PolicyDetailsBody from "./Body";
import { showPolicyDetailsDrawer } from "./Drawer";
import { GET_POLICY } from "./gql";
import PolicyDetailsSamples from "./Samples";
import { PolicyQueryResponse } from "./types";
import PolicyDetailsUsedBy from "./UsedBy";

const PolicyDetails = () => {
  const { tierFeatures } = useTypedContext(SubscriptionContext);

  const params = useParams<{ policyId?: string }>();

  const { policyId } = params;

  const takenPolicyNames = useTakenPolicyNames();

  const {
    loading: isPolicyLoading,
    data,
    networkStatus,
    error,
  } = useQuery<PolicyQueryResponse>(GET_POLICY, {
    variables: {
      policyId,
    },
  });

  const openDetailsDrawer = () => {
    if (data?.policy) {
      showPolicyDetailsDrawer({
        policy: data.policy,
        id: data.policy.id,
      });
    }
  };

  const policyType = data?.policy?.type;
  const requiredTierFeature = policyType && POLICY_TYPES_FEATURES[policyType];

  const isDisabledPolicy = requiredTierFeature && !tierFeatures.includes(requiredTierFeature);

  useBreadcrumbs(
    [
      {
        title: "Policies",
        link: getBreadcrumbsBackUrl("/policies", FILTERS_ORDER_SETTINGS_KEY),
      },
      {
        title: data?.policy?.name || "",
      },
    ],
    [data?.policy?.name]
  );

  const canManagePolicy = useCanManagePolicy()(data?.policy);

  const handleOpenEditDrawer = () => {
    showPolicyEditDrawer({
      policy: data?.policy,
      analyticsPage: AnalyticsPagePolicy.PolicyDetails,
      takenPolicyNames,
      refetchQueries: [
        {
          query: GET_POLICY,
          variables: {
            policyId: data?.policy.id,
          },
        },
      ],
    });
  };

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPolicyLoading && networkStatus !== NetworkStatus.refetch && !data?.policy) {
    return <PageLoading />;
  }

  if (!policyId || !data?.policy) {
    return <NotFoundPage />;
  }

  const policyUrl = `/policy/${data.policy.id}`;
  const policyUsedByUrl = `${policyUrl}/used-by`;
  const policySamplesUrl = `${policyUrl}/samples`;

  const usedByCount =
    data.policy.type === PolicyType.Intent
      ? data.policy.attachedIntentProjects.length
      : data.policy.attachedStacks.length;

  return (
    <>
      {isDisabledPolicy && (
        <Callout variant="danger">
          {`${POLICY_TYPES_TITLES[policyType]} policy will not be evaluated for your account. Upgrade your plan to make use of it.`}
          <FeedbackActions>
            <LinkButton to="/settings/billing" variant="contrast" size="small">
              Upgrade
            </LinkButton>
          </FeedbackActions>
        </Callout>
      )}
      <ViewHeader>
        <ViewHeaderWrapper direction="row" align="center" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{data.policy.name}</ViewHeaderTitle>
            <PolicyTypeBadge type={data.policy.type} />
          </ViewHeaderWrapper>
          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <DocumentationIconButton
              href={getDocsUrl("/concepts/policy")}
              tooltipText="Go to policies documentation"
            />
            <PolicyDetailsAskButton policyId={data.policy.id} />
            <Button variant="secondary" onPress={openDetailsDrawer}>
              Details
            </Button>
            <PolicyDetailsManageDropdown
              name={data.policy.name}
              id={data.policy.id}
              handlePolicyEditDetailsClick={handleOpenEditDrawer}
              size="medium"
              canManagePolicy={canManagePolicy}
              hasAttachedEntities={!!data.policy.hasAttachedStacks}
            />
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <SpacePath
              variant="meta"
              spaceId={data.policy.spaceDetails.id}
              fallbackName={data.policy.spaceDetails.name}
            />
            <TimestampMetaInfo prefix="Updated on" timestamp={data.policy.updatedAt} />
          </MetaInfoList>
        </ViewHeaderScrollCollapse>

        <ViewHeaderNavigation>
          <Tab end to={policyUrl} label="Policy body" />

          {data.policy.type !== PolicyType.Notification && (
            <TabWithCounter count={usedByCount} to={policyUsedByUrl} label="Used by" />
          )}
          {canManagePolicy.result && (
            <TabWithCounter count={data.policy.sampleCount} to={policySamplesUrl} label="Samples" />
          )}
        </ViewHeaderNavigation>
      </ViewHeader>

      <Routes>
        <Route
          index
          element={<PolicyDetailsBody policy={data.policy} initialBody={data.policy.body} />}
        />
        {data.policy.type !== PolicyType.Notification && data.policy.type !== PolicyType.Intent && (
          <Route
            path="used-by"
            element={
              <PolicyDetailsUsedBy
                name={data.policy.name}
                attachedItems={data.policy.attachedStacks}
                loading={isPolicyLoading}
              />
            }
          />
        )}
        {data.policy.type === PolicyType.Intent && (
          <Route
            path="used-by"
            element={
              <PolicyDetailsUsedBy
                name={data.policy.name}
                attachedItems={data.policy.attachedIntentProjects}
                loading={isPolicyLoading}
              />
            }
          />
        )}
        {canManagePolicy.result && (
          <Route path="samples" element={<PolicyDetailsSamples id={data.policy.id} />} />
        )}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
};

export default PolicyDetails;
